import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import SocialFeed from '../components/social/SocialFeed';
import WelcomeCarousel from '../components/dashboard/WelcomeCarousel';
import GettingStartedCard from '../components/dashboard/GettingStartedCard';
import QuickPlayWelcome from '../components/matchmaking/QuickPlayWelcome';
import ProfileCompletionCard from '../components/dashboard/ProfileCompletionCard';
import FeaturedVenues from '../components/dashboard/FeaturedVenues';
import UpcomingActivities from '../components/dashboard/UpcomingActivities';
import RecommendedConnections from '../components/dashboard/RecommendedConnections';
import TrendingPosts from '../components/dashboard/TrendingPosts';
import TrendingTopics from '../components/dashboard/TrendingTopics';
import LatestMembers from '../components/dashboard/LatestMembers';
import PopularCommunities from '../components/dashboard/PopularCommunities';
import QuickPlay from '../pages/QuickPlay';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Post } from '../types/post';
import { supabase } from '../lib/supabase';
import MostConnectedMembers from '../components/dashboard/MostConnectedMembers';
import TopCitiesLeaderboard from '../components/dashboard/TopCitiesLeaderboard';
// Import the notifications module to register the global function
import '../lib/supabase/notifications';

interface DashboardProps {
  defaultView?: 'feed' | 'quickplay';
}

const Dashboard: React.FC<DashboardProps> = ({ defaultView = 'quickplay' }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [focusedPost, setFocusedPost] = useState<Post | null>(null);
  const postId = searchParams.get('post');
  const [showQuickPlay, setShowQuickPlay] = useState(true);
  const [showEnhancedDashboard, setShowEnhancedDashboard] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [latestMembers, setLatestMembers] = useState<any[]>([]);
  const [popularCommunities, setPopularCommunities] = useState<any[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(true);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(true);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    window.scrollTo(0, 0);
    document.body.style.zoom = '1';
    document.body.style.transform = 'none';
    document.documentElement.style.transform = 'none';

    const shouldShowFeed = 
      !!postId || 
      (location.state && location.state.default === false) ||
      location.pathname.split('/').length > 2 ||
      location.pathname === '/dashboard/feed';

    setShowQuickPlay(
      !shouldShowFeed && 
      (location.pathname === '/dashboard/quick-play' || 
       (defaultView === 'quickplay' && location.pathname === '/dashboard'))
    );

    setShowEnhancedDashboard(location.pathname === '/dashboard' && !shouldShowFeed);
  }, [postId, location.pathname, location.state, defaultView]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowQuickPlay(prev => prev);
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) return;
      try {
        const { data, error } = await supabase
          .from('posts')
          .select(`
            id,
            content,
            media_url,
            likes_count,
            comments_count,
            shares_count,
            created_at,
            users (
              id,
              auth_id,
              full_name,
              avatar_url,
              city
            )
          `)
          .eq('id', postId)
          .single();
        if (error) throw error;
        if (!data) return;
        setFocusedPost({
          id: data.id,
          author: {
            id: data.users.auth_id,
            name: data.users.full_name,
            image:
              data.users.avatar_url ||
              'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png',
            location: data.users.city,
          },
          content: data.content,
          media: data.media_url,
          likes: data.likes_count,
          comments: data.comments_count,
          shares: data.shares_count,
          timeAgo: data.created_at,
        });
      } catch (error) {
        console.error('Error fetching post:', error);
      }
    };
    if (postId) {
      fetchPost();
      setShowQuickPlay(false);
    }
  }, [postId]);

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('quickplay_welcomed');
    setShowWelcome(!hasSeenWelcome);
  }, []);

  useEffect(() => {
    const fetchLatestMembers = async () => {
      if (!showEnhancedDashboard) return;
      setIsLoadingMembers(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data, error } = await supabase
          .from('users')
          .select('auth_id, full_name, avatar_url, city, job_type, company')
          .neq('auth_id', user.id)
          .order('created_at', { ascending: false })
          .limit(10);
        if (error) throw error;
        setLatestMembers(data || []);
      } catch (error) {
        console.error('Error fetching latest members:', error);
      } finally {
        setIsLoadingMembers(false);
      }
    };
    fetchLatestMembers();
  }, [showEnhancedDashboard]);

  useEffect(() => {
    const fetchPopularCommunities = async () => {
      if (!showEnhancedDashboard) return;
      setIsLoadingCommunities(true);
      try {
        const { data, error } = await supabase
          .rpc('get_communities_by_member_count', { limit_param: 10 });
        if (error) throw error;
        setPopularCommunities(data || []);
      } catch (error) {
        console.error('Error fetching popular communities:', error);
      } finally {
        setIsLoadingCommunities(false);
      }
    };
    fetchPopularCommunities();
  }, [showEnhancedDashboard]);

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).registerPushToken) {
      console.log("Global registerPushToken function is available.");
    }
    if (window.AndroidInterface?.onLoginComplete) {
      console.log("Calling onLoginComplete on AndroidInterface");
      window.AndroidInterface.onLoginComplete();
    } else if (
      window.webkit?.messageHandlers?.onLoginComplete
    ) {
      console.log("Calling onLoginComplete on iOS interface");
      window.webkit.messageHandlers.onLoginComplete.postMessage(null);
    }
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-[1400px] mx-auto min-h-[calc(100vh-9rem)]">
        {showEnhancedDashboard ? (
          <>
            <WelcomeCarousel />
            <QuickPlayWelcome />
            <GettingStartedCard />
            <ProfileCompletionCard />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <TrendingPosts />
                <LatestMembers members={latestMembers} isLoading={isLoadingMembers} />
                <FeaturedVenues />
                <RecommendedConnections />
                <MostConnectedMembers />
                <UpcomingActivities />
                <TrendingTopics />
              </div>
              <div className="space-y-6">
                <PopularCommunities communities={popularCommunities} isLoading={isLoadingCommunities} />
                <TopCitiesLeaderboard />
              </div>
            </div>
          </>
        ) : showQuickPlay ? (
          <QuickPlay />
        ) : (
          <SocialFeed focusedPost={focusedPost} />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;